// The Asset library's shared rules and calls (HD-10). Used by the Console page (pages/AssetLibrary.jsx), the Portal page
// (pages/assets/PortalAssets.jsx), the record view (components/AssetRecord.jsx) and the Clio chat.
// The role lists are the pre-HD-10 AssetCardModal's; the API enforces its own (routes/assets.js, routes/assetFolders.js).
import api from './api';

const UPLOAD_ROLES = ['Editor', 'Producer', 'Post Lead', 'Post_Lead', 'Power User', 'Power_User', 'Admin'];
const ADMIN_ROLES  = ['Admin', 'Power User', 'Power_User'];
export const PROP_TYPES = ['costume', 'branded merch', 'set dressing', 'hand prop', 'food', 'other'];
export const TAG_KEYS = [
  { key: 'objects', label: 'Objects', one: 'object' },
  { key: 'brands',  label: 'Brands',  one: 'brand' },
  { key: 'themes',  label: 'Themes',  one: 'theme' },
  { key: 'colors',  label: 'Colors',  one: 'color' },
];

export function userCanUpload(user) { return !!user?.roles?.some(r => UPLOAD_ROLES.includes(r)); }
export function userIsAdmin(user)   { return !!user?.roles?.some(r => ADMIN_ROLES.includes(r)); }

// A prop type as a sentence-case label ("branded merch" → "Branded merch").
export const propLabel = t => (t ? t.charAt(0).toUpperCase() + t.slice(1) : '');
export const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function fmtBytes(b) {
  if (!b) return null;
  const n = Number(b);
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.round(n / 1024)} KB`;
}
export function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// GET /api/assets query, in the old page's parameter order (page, q, prop_type, client_id, folder_id).
export function listParams({ page, q, propType, clientId, folderId }) {
  const params = new URLSearchParams({ page });
  if (q)        params.set('q', q);
  if (propType) params.set('prop_type', propType);
  if (clientId) params.set('client_id', clientId);
  if (folderId) params.set('folder_id', folderId);
  return params;
}

// ── Uploads (through the global Upload sheet, shell/uploads.js) ──────────────────────────────────────────────────

export const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
export const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/heic,.heic';
export const isImageFile = f => f.type.startsWith('image/') || f.name.toLowerCase().endsWith('.heic');

// One image: POST /api/assets/presign { fileName, fileSize, contentType } → PUT the file to the presigned URL →
// POST /api/assets/confirm { assetId, folderId, clientId }. The upload's result is the asset id.
export function imageTransfer(file, onProgress) {
  const promise = (async () => {
    const { data } = await api.post('/api/assets/presign', {
      fileName:    file.name,
      fileSize:    file.size,
      contentType: file.type || 'image/jpeg',
    });
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener('progress', e => { if (e.lengthComputable) onProgress(e.loaded, e.total); });
      xhr.addEventListener('load', () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`The upload stopped (HTTP ${xhr.status}). Retry it.`))));
      xhr.addEventListener('error', () => reject(new Error('The connection dropped. Retry the upload.')));
      xhr.open('PUT', data.uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'image/jpeg');
      xhr.send(file);
    });
    return data.assetId;
  })();
  return { promise };
}

export const confirmImage = ({ folderId, clientId }) => async assetId => {
  await api.post('/api/assets/confirm', { assetId, folderId: folderId || null, clientId: clientId || null });
  return { assetId };
};

// A ZIP of images: POST /api/assets/upload-zip (multipart form: zip, folderId when set). The result is the API's summary
// { queued, skipped, over_limit }.
export function zipTransfer(folderId) {
  return (file, onProgress) => {
    const fd = new FormData();
    fd.append('zip', file);
    if (folderId) fd.append('folderId', folderId);
    const promise = api.post('/api/assets/upload-zip', fd, {
      onUploadProgress: e => { if (e.total) onProgress(e.loaded, e.total); },
    }).then(r => r.data);
    return { promise };
  };
}
